import { useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/Components/ui/dialog';
import { Button } from '@/Components/ui/button';
import { FileDown } from 'lucide-react';

const RASTER_SIZE = 1024;

function svgMarkup(svgElement) {
    const clone = svgElement.cloneNode(true);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    return new XMLSerializer().serializeToString(clone);
}

function triggerDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // Revoking in the same tick cancels the download in Firefox and Safari.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Draw the QR svg onto a white canvas with a quiet zone, for raster formats.
async function rasterizeQr(svgElement, size = RASTER_SIZE) {
    const image = new Image();
    await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
        image.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgMarkup(svgElement));
    });
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, size, size);
    const padding = size * 0.05;
    context.drawImage(image, padding, padding, size - padding * 2, size - padding * 2);
    return canvas;
}

function canvasToBlob(canvas, type, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Canvas export failed'))), type, quality);
    });
}

/**
 * Shows a QR code for `url` in a dialog, downloadable as PNG, SVG, PDF or
 * JPG. Wrap the trigger element: <QrCodeDialog url={...}><Button/></QrCodeDialog>
 */
export default function QrCodeDialog({ url, fileBaseName = 'qr-code', title = 'QR Code', description, children }) {
    const qrContainerRef = useRef(null);
    // Rasterizing and, for PDF, loading jspdf take long enough to look broken
    // without a busy state on the button that was pressed.
    const [busyFormat, setBusyFormat] = useState(null);

    async function download(format) {
        const svgElement = qrContainerRef.current?.querySelector('svg');
        if (!svgElement || busyFormat) return;
        setBusyFormat(format);
        try {
            if (format === 'svg') {
                triggerDownload(new Blob([svgMarkup(svgElement)], { type: 'image/svg+xml' }), `${fileBaseName}.svg`);
                return;
            }
            const canvas = await rasterizeQr(svgElement);
            if (format === 'png') {
                triggerDownload(await canvasToBlob(canvas, 'image/png'), `${fileBaseName}.png`);
            }
            if (format === 'jpg') {
                triggerDownload(await canvasToBlob(canvas, 'image/jpeg', 0.95), `${fileBaseName}.jpg`);
            }
            if (format === 'pdf') {
                const { jsPDF } = await import('jspdf');
                const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
                pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 55, 70, 100, 100);
                pdf.setFontSize(10);
                pdf.text(url, 105, 180, { align: 'center' });
                pdf.save(`${fileBaseName}.pdf`);
            }
        } catch (error) {
            console.error('QR download failed:', error);
            toast.error('Failed to generate the QR download');
        } finally {
            setBusyFormat(null);
        }
    }

    return (
        <Dialog>
            <DialogTrigger asChild>{children}</DialogTrigger>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>{description ?? 'Scanning this code opens the public passport page.'}</DialogDescription>
                </DialogHeader>
                <div className="flex justify-center p-6 bg-white border rounded-lg" ref={qrContainerRef}>
                    <QRCode value={url} size={220} aria-label={`QR code for ${url}`} />
                </div>
                <p className="font-mono text-xs text-center break-all text-muted-foreground">{url}</p>
                <DialogFooter className="flex-wrap gap-2 sm:justify-center">
                    {['png', 'svg', 'pdf', 'jpg'].map((format) => (
                        <Button
                            key={format}
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={busyFormat !== null}
                            aria-label={`Download as ${format.toUpperCase()}`}
                            onClick={() => download(format)}
                        >
                            <FileDown className="w-4 h-4 mr-1" />
                            {busyFormat === format ? 'Preparing…' : format.toUpperCase()}
                        </Button>
                    ))}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
